/**
 * AdminReportDeliveriesPage
 *
 * Queue-driven approval flow for B2B partner reports.
 *
 * PDFs are NEVER auto-sent — the scheduler generates DRAFTS which land here,
 * and the admin must explicitly click Send after previewing.
 *
 * Actions: [Preview] [Send] [Skip] [Discard]
 * Filters by status; badge counts in the header.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, RefreshCw, Eye, Send, SkipForward, Trash2, PlayCircle,
  Clock, CheckCircle, XCircle, AlertTriangle, Building2, Mail, Beaker,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_FILTERS = [
  { key: 'pending_approval', label: 'Pending', icon: Clock, tone: 'amber' },
  { key: 'sent', label: 'Sent', icon: CheckCircle, tone: 'green' },
  { key: 'send_failed', label: 'Send Failed', icon: AlertTriangle, tone: 'red' },
  { key: 'skipped', label: 'Skipped', icon: SkipForward, tone: 'slate' },
  { key: 'discarded', label: 'Discarded', icon: XCircle, tone: 'slate' },
];

const toneClass = {
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  green: 'bg-green-50 text-green-700 border-green-200',
  red: 'bg-red-50 text-red-700 border-red-200',
  slate: 'bg-slate-50 text-slate-600 border-slate-200',
};

const shortDate = (iso) => (iso ? iso.slice(0, 10) : '—');

const AdminReportDeliveriesPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('pending_approval');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [sendModal, setSendModal] = useState(null); // delivery object
  const [sendNotes, setSendNotes] = useState('');
  const [tickingScheduler, setTickingScheduler] = useState(false);

  const authHeader = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/report-deliveries`, {
        headers: authHeader,
        params: { status: filter, limit: 200 },
      });
      setItems(res.data.items || []);
      setCounts(res.data.counts_by_status || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load deliveries');
    } finally {
      setLoading(false);
    }
  }, [token, filter, authHeader]);

  useEffect(() => { load(); }, [load]);

  const openPreview = async (delivery) => {
    // Fetch as blob so we can inline-preview even though it's admin-protected
    try {
      const res = await axios.get(`${API}/admin/report-deliveries/${delivery.delivery_id}/pdf`, {
        headers: authHeader,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to preview PDF');
    }
  };

  const sendTestToMe = async (delivery) => {
    setBusyId(delivery.delivery_id);
    try {
      const res = await axios.post(
        `${API}/admin/report-deliveries/${delivery.delivery_id}/send-test`,
        {},
        { headers: authHeader },
      );
      toast.success(`Test sent to ${res.data.test_to} — real recipients not touched`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Test send failed');
    } finally {
      setBusyId(null);
    }
  };

  const confirmSend = async () => {
    if (!sendModal) return;
    setBusyId(sendModal.delivery_id);
    try {
      const res = await axios.post(
        `${API}/admin/report-deliveries/${sendModal.delivery_id}/send`,
        { notes: sendNotes || null },
        { headers: authHeader },
      );
      if (res.data.status === 'sent') {
        toast.success(`Report sent to ${res.data.results.length} recipient(s)`);
      } else {
        const failed = (res.data.results || []).filter((r) => !r.sent);
        toast.error(`Some sends failed: ${failed.map((f) => f.email).join(', ')}`);
      }
      setSendModal(null);
      setSendNotes('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Send failed');
    } finally {
      setBusyId(null);
    }
  };

  const skip = async (delivery) => {
    const reason = window.prompt('Reason for skipping (optional):') ?? '';
    setBusyId(delivery.delivery_id);
    try {
      await axios.post(
        `${API}/admin/report-deliveries/${delivery.delivery_id}/skip`,
        { reason: reason || null },
        { headers: authHeader },
      );
      toast.success('Delivery skipped');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Skip failed');
    } finally {
      setBusyId(null);
    }
  };

  const discard = async (delivery) => {
    if (!window.confirm('Discard this delivery draft? This cannot be undone.')) return;
    setBusyId(delivery.delivery_id);
    try {
      await axios.delete(`${API}/admin/report-deliveries/${delivery.delivery_id}`, {
        headers: authHeader,
      });
      toast.success('Delivery discarded');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Discard failed');
    } finally {
      setBusyId(null);
    }
  };

  const runSchedulerNow = async () => {
    if (!window.confirm('Run the draft-generation scheduler right now?\n\nThis will scan all active subscriptions and generate draft reports for any whose cadence is due. Nothing gets emailed — drafts land in this queue.')) return;
    setTickingScheduler(true);
    try {
      await axios.post(`${API}/admin/report-deliveries/scheduler/tick`, {}, { headers: authHeader });
      toast.success('Scheduler tick complete');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Tick failed');
    } finally {
      setTickingScheduler(false);
    }
  };

  const isPending = (d) => d.status === 'pending_approval' || d.status === 'send_failed';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-report-deliveries-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <button
            onClick={() => navigate('/admin')}
            data-testid="deliveries-back-btn"
            className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm mb-4"
          >
            <ArrowLeft className="w-4 h-4" /> Back to admin
          </button>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-black tracking-tight text-slate-900">Report Deliveries</h1>
              <p className="text-slate-600 text-sm mt-1">
                Generated drafts stay here until you explicitly click Send.
                <strong className="text-slate-900"> Nothing is ever emailed automatically.</strong>
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={runSchedulerNow}
                disabled={tickingScheduler}
                data-testid="run-scheduler-btn"
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 disabled:opacity-50"
              >
                <PlayCircle className={`w-4 h-4 ${tickingScheduler ? 'animate-spin' : ''}`} />
                Run Scheduler Now
              </button>
              <button
                onClick={load}
                data-testid="deliveries-refresh-btn"
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-sm hover:bg-slate-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Status filter pills */}
      <div className="max-w-6xl mx-auto px-6 pt-6">
        <div className="flex flex-wrap gap-2 mb-6" data-testid="status-filter">
          {STATUS_FILTERS.map(({ key, label, icon: Icon, tone }) => {
            const active = filter === key;
            const count = counts[key] ?? 0;
            return (
              <button
                key={key}
                onClick={() => setFilter(key)}
                data-testid={`status-filter-${key}`}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-bold transition-all ${
                  active
                    ? 'bg-slate-900 text-white border-slate-900'
                    : `bg-white text-slate-700 border-slate-200 hover:bg-slate-50 ${toneClass[tone]}`
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
                <span className={`text-xs rounded-full px-2 py-0.5 ${active ? 'bg-white/20' : 'bg-slate-100'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-16 text-slate-500" data-testid="deliveries-loading">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 text-slate-500 bg-white rounded-xl border border-slate-200" data-testid="deliveries-empty">
            No {STATUS_FILTERS.find((f) => f.key === filter)?.label?.toLowerCase()} deliveries.
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm" data-testid="deliveries-list">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase text-slate-500 tracking-wide">
                  <th className="px-4 py-3">Organization / Tour</th>
                  <th className="px-4 py-3">Period</th>
                  <th className="px-4 py-3">Generated</th>
                  <th className="px-4 py-3">Recipients</th>
                  <th className="px-4 py-3">Trigger</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((d) => (
                  <tr key={d.delivery_id} data-testid={`delivery-row-${d.delivery_id}`} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="font-black text-slate-900 flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        {d.org_name}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{d.tour_title}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                      {shortDate(d.period_start)} → {shortDate(d.period_end)}
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{shortDate(d.generated_at)}</td>
                    <td className="px-4 py-3 text-slate-700">
                      <div className="flex items-center gap-1">
                        <Mail className="w-3 h-3 text-slate-400" />
                        {(d.recipients || []).length}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex text-xs font-bold px-2 py-1 rounded-full ${
                        d.trigger === 'manual' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'
                      }`}>
                        {d.trigger === 'manual' ? 'Manual' : 'Scheduled'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => openPreview(d)}
                          data-testid={`preview-btn-${d.delivery_id}`}
                          className="p-2 rounded-lg text-slate-600 hover:bg-slate-100"
                          title="Preview PDF"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {isPending(d) && (
                          <>
                            <button
                              onClick={() => sendTestToMe(d)}
                              disabled={busyId === d.delivery_id}
                              data-testid={`send-test-btn-${d.delivery_id}`}
                              className="p-2 rounded-lg text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                              title="Send test to my email (real recipients not touched)"
                            >
                              <Beaker className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setSendModal(d)}
                              disabled={busyId === d.delivery_id}
                              data-testid={`send-btn-${d.delivery_id}`}
                              className="p-2 rounded-lg text-green-700 hover:bg-green-50 disabled:opacity-50"
                              title="Send to recipients"
                            >
                              <Send className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => skip(d)}
                              disabled={busyId === d.delivery_id}
                              data-testid={`skip-btn-${d.delivery_id}`}
                              className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 disabled:opacity-50"
                              title="Skip this cycle"
                            >
                              <SkipForward className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => discard(d)}
                              disabled={busyId === d.delivery_id}
                              data-testid={`discard-btn-${d.delivery_id}`}
                              className="p-2 rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-50"
                              title="Discard draft"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Send confirmation modal */}
      {sendModal && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          data-testid="send-modal"
        >
          <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl">
            <div className="px-6 py-5 border-b border-slate-200">
              <h2 className="text-xl font-black text-slate-900">Send this report?</h2>
              <p className="text-sm text-slate-600 mt-1">
                Once sent, the PDF is emailed to the recipients below and this action can&apos;t be undone.
              </p>
            </div>
            <div className="px-6 py-5">
              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <div className="text-xs uppercase text-slate-500 font-bold mb-2">Recipients</div>
                <ul className="text-sm text-slate-800 space-y-1" data-testid="send-recipients-list">
                  {(sendModal.recipients || []).map((r, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Mail className="w-3 h-3 text-slate-400" />
                      {r.name ? `${r.name} <${r.email}>` : r.email}
                    </li>
                  ))}
                </ul>
              </div>
              <label className="block">
                <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">
                  Optional note to include in the email body
                </span>
                <textarea
                  value={sendNotes}
                  onChange={(e) => setSendNotes(e.target.value)}
                  rows={3}
                  maxLength={500}
                  placeholder="e.g. Highlights from this month: sponsor interest at the Jaffa gate stop..."
                  data-testid="send-notes-input"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none resize-none"
                />
              </label>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-between items-center gap-2 flex-wrap">
              <button
                onClick={async () => {
                  await sendTestToMe(sendModal);
                }}
                disabled={busyId === sendModal.delivery_id}
                data-testid="send-test-from-modal-btn"
                className="inline-flex items-center gap-2 text-sm font-bold text-amber-700 hover:text-amber-900 disabled:opacity-50"
              >
                <Beaker className="w-4 h-4" />
                Send test to me first
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => { setSendModal(null); setSendNotes(''); }}
                  data-testid="send-cancel-btn"
                  className="px-4 py-2 rounded-lg text-sm font-bold text-slate-700 hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSend}
                  disabled={busyId === sendModal.delivery_id}
                  data-testid="send-confirm-btn"
                  className="px-4 py-2 rounded-lg text-sm font-bold bg-slate-900 text-white hover:bg-black disabled:opacity-50 flex items-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  {busyId === sendModal.delivery_id ? 'Sending…' : 'Send to recipients'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReportDeliveriesPage;
